<template>
    <v-dialog
        :value="open"
        max-width="900px"
        persistent
        @input="$emit('closeDialog')"
    >
        <v-card>
            <v-card-title class="headline">
                <span>Link Invoice to Purchase</span>
            </v-card-title>
            <v-card-text>
                <div class="mb-4">
                    <p class="text-body-1 mb-2">
                        <strong>Invoice Details:</strong>
                    </p>
                    <p class="text-body-2">
                        Supplier: {{ noOrderItem?.supplier }}
                    </p>
                    <p class="text-body-2">
                        Total Price: {{ formatNumber(noOrderItem?.totalPrice) }}
                    </p>
                    <p class="text-body-2">
                        Due Date: {{ noOrderItem?.dueDate }}
                    </p>
                </div>
                <v-divider class="my-4" />
                <v-autocomplete
                    v-model="selectedPurchaseIds"
                    :items="availablePurchases"
                    item-text="displayText"
                    item-value="id"
                    label="Select Purchases"
                    class="mb-4"
                    full-width
                    outlined
                    hide-details
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    cache-items
                    menu-props="{
                        maxHeight: '300px',
                        'min-width': '100%'
                    }"
                    :item-height="48"
                    @change="onPurchaseChange"
                >
                    <template v-slot:item="{ item, on, attrs }">
                        <v-list-item
                            v-bind="attrs"
                            v-on="on"
                            style="min-height: 48px;"
                        >
                            <v-list-item-content>
                                <v-list-item-title style="font-size: 14px;">
                                    {{ item.displayText }}
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                </v-autocomplete>
                <!-- Table for selected purchases with order number selectors -->
                <v-data-table
                    v-if="selectedPurchases.length > 0"
                    :headers="purchaseTableHeaders"
                    :items="selectedPurchases"
                    :items-per-page="-1"
                    hide-default-footer
                    class="mb-4"
                >
                    <template v-slot:[`item.purchaseInfo`]="{ item }">
                        <div>
                            {{ item.displayText }}
                        </div>
                    </template>
                    <template v-slot:[`item.orderNumber`]="{ item }">
                        <v-select
                            class="my-2"
                            v-model="selectedOrderNumbers[item.id]"
                            :items="item.orderNumbers || []"
                            label="Select Order Number"
                            dense
                            hide-details
                            :rules="[v => !!v || 'Order Number is required']"
                            required
                        />
                    </template>
                </v-data-table>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog" :disabled="loading">
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    @click="confirmLinkInvoice"
                    :loading="loading"
                    :disabled="loading || !allPurchasesHaveOrderNumber"
                >
                    Link Invoice
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import API from '@/services/api'
import { mapMutations } from 'vuex'

export default {
    name: 'LinkInvoice',
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        noOrderItem: {
            type: Object,
            required: true,
        },
        availablePurchases: {
            type: Array,
            default: () => [],
        },
        companyId: {
            type: String,
            required: true,
        },
        requests: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            selectedPurchaseIds: [],
            selectedOrderNumbers: {},
            loading: false,
            purchaseTableHeaders: [
                {
                    text: 'Purchase',
                    value: 'purchaseInfo',
                    sortable: false,
                },
                {
                    text: 'Order Number',
                    value: 'orderNumber',
                    sortable: false,
                },
            ],
        }
    },
    computed: {
        selectedPurchases() {
            return this.selectedPurchaseIds
                .map(id =>
                    this.availablePurchases.find(purchase => purchase.id === id)
                )
                .filter(Boolean)
        },
        allPurchasesHaveOrderNumber() {
            if (
                !this.selectedPurchaseIds ||
                this.selectedPurchaseIds.length === 0
            ) {
                return false
            }
            return this.selectedPurchaseIds.every(
                purchaseId =>
                    this.selectedOrderNumbers[purchaseId] &&
                    this.selectedOrderNumbers[purchaseId].trim() !== ''
            )
        },
    },
    watch: {
        open(isOpen) {
            if (isOpen) {
                this.selectedPurchaseIds = []
                this.selectedOrderNumbers = {}
            }
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        formatNumber(number) {
            return new Intl.NumberFormat('de-DE').format(
                Math.ceil(number != undefined ? number : 0)
            )
        },
        closeDialog() {
            this.selectedPurchaseIds = []
            this.selectedOrderNumbers = {}
            this.$emit('closeDialog')
        },
        onPurchaseChange() {
            Object.keys(this.selectedOrderNumbers).forEach(purchaseId => {
                if (!this.selectedPurchaseIds.includes(purchaseId)) {
                    delete this.selectedOrderNumbers[purchaseId]
                }
            })
        },
        async confirmLinkInvoice() {
            if (
                !this.selectedPurchaseIds ||
                this.selectedPurchaseIds.length === 0 ||
                !this.allPurchasesHaveOrderNumber ||
                !this.noOrderItem
            ) {
                return
            }

            try {
                this.loading = true

                // Get file from item
                // If array: last item is filename, rest are pastFiles
                // If string: use as filename, no pastFiles
                let invoiceFile = null
                let pastFiles = []

                if (
                    Array.isArray(this.noOrderItem.file) &&
                    this.noOrderItem.file.length > 0
                ) {
                    // Last item is the current filename
                    invoiceFile = this.noOrderItem.file[
                        this.noOrderItem.file.length - 1
                    ]
                    // All items except the last are pastFiles
                    if (this.noOrderItem.file.length > 1) {
                        pastFiles = this.noOrderItem.file.slice(0, -1)
                    }
                } else if (
                    this.noOrderItem.file &&
                    typeof this.noOrderItem.file === 'string' &&
                    this.noOrderItem.file.trim() !== ''
                ) {
                    invoiceFile = this.noOrderItem.file
                }

                if (!invoiceFile) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: 'Invoice file not found',
                    })
                    return
                }

                // Prepare invoice data
                // Get createdOn from noOrderItem
                let createdOnDate = new Date().toISOString()

                if (this.noOrderItem.createdOn) {
                    if (this.noOrderItem.createdOn.seconds) {
                        createdOnDate = new Date(
                            this.noOrderItem.createdOn.seconds * 1000
                        ).toISOString()
                    } else if (this.noOrderItem.createdOn._seconds) {
                        createdOnDate = new Date(
                            this.noOrderItem.createdOn._seconds * 1000
                        ).toISOString()
                    } else if (typeof this.noOrderItem.createdOn === 'string') {
                        createdOnDate = this.noOrderItem.createdOn
                    }
                }

                let invoiceData = {
                    id: this.noOrderItem.id,
                    file: invoiceFile,
                    supplier: this.noOrderItem.supplier,
                    dueDate: this.noOrderItem.dueDate,
                    totalPrice: parseFloat(this.noOrderItem.totalPrice) || 0,
                    createdOn: createdOnDate,
                    pastFiles: pastFiles,
                    notes: this.noOrderItem.notes || [],
                }
                if (this.noOrderItem.consecutive) {
                    invoiceData.consecutive = this.noOrderItem.consecutive
                }
                if (this.noOrderItem.creditNote) {
                    invoiceData.creditNote = true
                    invoiceData.creditNotePreviousConsecutive =
                        this.noOrderItem.creditNotePreviousConsecutive ?? null
                }

                const purchases = this.selectedPurchaseIds.map(purchaseId => ({
                    purchaseId,
                    orderNumber: this.selectedOrderNumbers[purchaseId],
                }))

                // Link invoice to purchases via API
                await API.linkNoOrderPurchaseInvoiceToPurchase({
                    companyId: this.companyId,
                    purchases: purchases,
                    invoice: invoiceData,
                })

                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message:
                        error.message || 'Error linking invoice to purchases',
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>
